/**
 * router/routes/sumi.routes.ts
 * SUMI (Proveedores) module routes — the sumi/suppliers home + Cuentas a
 * Pagar section, nested under the tenant AppShell (sumiRoutes), PLUS the
 * legacy top-level `/cuentas-pagar/*` redirects (cuentasPagarLegacyRedirects)
 * that keep old bookmarked/prod links working (reorganizar-cuentas-pagar-
 * bajo-sumi, task 8.8a) — those are NOT nested (they render fullscreen before
 * resolving), so router/index.ts spreads them into its TOP-LEVEL routes
 * array, not into the AppShell children.
 * Split out of router/index.ts (openspec/arquitectura/convencion-modulo.md §4).
 */
import type { RouteRecordRaw } from 'vue-router';

// add-sumi-proveedores-abm's 'suppliers' route is a SUMI screen, but is kept in
// admin.routes.ts — it sits between 'client-detail' and 'contractors' in the
// original source, and the vue-router matcher breaks same-score ties by
// registration order, so moving it here (next to sumi-home) would have
// reordered it relative to those routes in getRoutes() (caught by
// routes-characterization.spec.ts). Resolution is identical either way; this
// placement keeps the exact original tie-break order.

export const sumiRoutes: RouteRecordRaw[] = [
  // SUMI shell: /sumi — Proveedores module landing (navigable empty shell).
  // No meta.requiresCapability — SUMI has no capability vocabulary yet;
  // entitlement gates visibility via the nav manifest + dashboard card.
  {
    path: 'sumi',
    name: 'sumi-home',
    component: () => import('../../views/sumi/SumiHomeView.vue'),
  },

  // add-cuentas-pagar-ui Phase 3 (design.md D4 routes table, task 3.5) —
  // "Facturas a pagar", the tesorero's main AP screen. Capability mirrors
  // the backend guard on GET /sumi/cuentas-pagar/aging (cuentas-pagar-http-
  // routes APR-5). Entitlement is resolved via the `sumi` module flag
  // (reorganizar-cuentas-pagar-bajo-sumi, C7 — AP's own
  // `module.cuentas_pagar.enabled` flag was retired) server-side via
  // /nav/config (APU-1.1) — this meta only mirrors the per-endpoint
  // capability, same posture as every other requiresCapability route above.
  {
    path: 'sumi/cuentas-pagar',
    name: 'cuentas-pagar-aging',
    component: () => import('../../views/sumi/cuentas-pagar/CuentasPagarAgingView.vue'),
    meta: { requiresCapability: 'sumi.saldo.leer' },
  },

  // The old "Facturas y notas" screen (2026-10-16): folded into "Facturas a pagar" (estado filter + editar/anular).
  {
    path: 'sumi/cuentas-pagar/comprobantes',
    redirect: (to) => ({ name: 'cuentas-pagar-aging', query: to.query }),
  },

  // cuentas-pagar-historial-y-gestion-pagos Phase 4 (design.md D4/File
  // Changes, task 4.9) — "Historial de pagos" (APR-8/APU-7): todas las
  // órdenes de pago emitidas, filtrables por proveedor/fecha/medio de
  // pago, cada fila con descarga directa de su Orden de Pago PDF.
  // Capability mirrors the backend guard on GET /sumi/cuentas-pagar/pagos
  // (cuentas-pagar-http-routes APR-8).
  {
    path: 'sumi/cuentas-pagar/pagos',
    name: 'cuentas-pagar-pagos',
    component: () => import('../../views/sumi/cuentas-pagar/HistorialPagosView.vue'),
    meta: { requiresCapability: 'sumi.saldo.leer' },
  },

  // T4 (odd/tasks/cuentas-pagar-detalle-imputacion-reporte.md) — proveedor
  // "cuenta corriente" (saldo/vencido/condición de pago + movimientos).
  // Reached ONLY by clicking a row in the "Facturas a pagar" grid
  // (AgingTable.vue's `@select`, wired in CuentasPagarAgingView.vue) —
  // deliberately NOT in manifest.ts/KERNEL_NAV_REGISTRY, same posture as
  // `client-detail`/`compras-detail`/`ats-detail` elsewhere in this file
  // (`<module>/:id` path, `props: true`). Capability mirrors the same
  // backend guard as the aging/saldo GETs (`sumi.saldo.leer`).
  {
    path: 'sumi/cuentas-pagar/proveedores/:id',
    name: 'cuentas-pagar-proveedor-detalle',
    component: () => import('../../views/sumi/cuentas-pagar/SupplierDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'sumi.saldo.leer' },
  },

  // Cronograma de pagos (2026-10-15): the plans, and one plan (invoices by proveedor, print, confirm the paid ones).
  // Capability mirrors GET /sumi/cuentas-pagar/planes-pago (`sumi.saldo.leer`); the actions check `sumi.imputacion.crear`.
  {
    path: 'sumi/cuentas-pagar/cronogramas',
    name: 'cuentas-pagar-cronogramas',
    component: () => import('../../views/sumi/cuentas-pagar/CronogramasView.vue'),
    meta: { requiresCapability: 'sumi.saldo.leer' },
  },
  {
    path: 'sumi/cuentas-pagar/cronogramas/:id',
    name: 'cuentas-pagar-cronograma',
    component: () => import('../../views/sumi/cuentas-pagar/CronogramaView.vue'),
    meta: { requiresCapability: 'sumi.saldo.leer' },
  },

  // ap-pagar-factura-directo (T2) — "Pagar factura": pays ONE invoice at once (suggested retentions, live A PAGAR).
  // Reached from the "Pagar" button of an invoice row (Facturas a pagar, proveedor detail); deliberately NOT in the nav.
  // Capability mirrors POST /sumi/cuentas-pagar/comprobantes/:id/pagar (`sumi.imputacion.crear`).
  {
    path: 'sumi/cuentas-pagar/facturas/:id/pagar',
    name: 'cuentas-pagar-pagar-factura',
    component: () => import('../../views/sumi/cuentas-pagar/PagarFacturaView.vue'),
    meta: { requiresCapability: 'sumi.imputacion.crear' },
  },

  // The old "Imputar pago" screen is gone (retenciones-en-el-pago D13): a payment is registered from "Pagar factura"
  // (above), one invoice at a time. The legacy `/sumi/cuentas-pagar/proveedores/:id/imputar` URL lands on the list of
  // invoices to pay instead of a 404.
  {
    path: 'sumi/cuentas-pagar/proveedores/:id/imputar',
    redirect: { name: 'cuentas-pagar-aging' },
  },

  // add-cuentas-pagar-ui Phase 4 (design.md D4 routes table, task 4.4) —
  // "Cargar comprobante", the tesorero's alta screen. Capability mirrors
  // the backend guard on POST /sumi/cuentas-pagar/comprobantes
  // (cuentas-pagar-http-routes APR-1). Route name 'comprobante-new'
  // matches CuentasPagarAgingView.vue's own `router.push({ name:
  // 'comprobante-new' })` on its "+ Cargar comprobante" primary action.
  {
    path: 'sumi/cuentas-pagar/comprobantes/nuevo',
    name: 'comprobante-new',
    component: () => import('../../views/sumi/cuentas-pagar/ComprobanteCreateView.vue'),
    meta: { requiresCapability: 'sumi.comprobante.crear' },
  },
  // ap-anular-editar-factura — same form in edit mode (only a comprobante without payments; the backend enforces it).
  {
    path: 'sumi/cuentas-pagar/comprobantes/:id/editar',
    name: 'comprobante-edit',
    component: () => import('../../views/sumi/cuentas-pagar/ComprobanteCreateView.vue'),
    props: true,
    meta: { requiresCapability: 'sumi.comprobante.crear' },
  },

  // add-cuentas-pagar-ui Phase 6 (design.md D4 routes table, task 6.3) —
  // "Configuración", the tesorero's AP settings + tipo-de-retención
  // catalog screen. Capability mirrors the backend guard on GET/PUT
  // /sumi/cuentas-pagar/config and /sumi/cuentas-pagar/retenciones/tipos
  // (cuentas-pagar-http-routes APR-6). Route name 'cuentas-pagar-config'.
  {
    path: 'sumi/cuentas-pagar/configuracion',
    name: 'cuentas-pagar-config',
    component: () => import('../../views/sumi/cuentas-pagar/CuentasPagarConfigView.vue'),
    meta: { requiresCapability: 'sumi.ap-config.administrar' },
  },
];

// Legacy top-level `/cuentas-pagar/*` redirects — see the comment on the
// first entry below for the full rationale.
export const cuentasPagarLegacyRedirects: RouteRecordRaw[] = [
  // reorganizar-cuentas-pagar-bajo-sumi, task 8.8a (user decision 2026-10-05,
  // prod-usage-verification.md "Decision (task 0.5)") — real ledger rows and
  // assigned users exist in prod for the OLD `/cuentas-pagar/*` URLs, so
  // saved/bookmarked links must keep working after this change moves every
  // AP route under `/sumi/cuentas-pagar/*`. FRONTEND-ONLY: the backend API
  // has NO alias (design D8) — these `redirect` entries resolve BEFORE the
  // auth guard runs (Vue Router follows `redirect` before `beforeEach`), so
  // the final guard check applies to the NEW route's own meta, same as
  // navigating there directly. Each uses a function form (not a static
  // object) to explicitly forward `params`/`query` — Vue Router does not
  // guarantee carrying those over for a bare `{ name }` redirect target.
  {
    path: '/cuentas-pagar',
    redirect: (to) => ({ name: 'cuentas-pagar-aging', query: to.query }),
  },

  {
    path: '/cuentas-pagar/comprobantes',
    redirect: (to) => ({ name: 'cuentas-pagar-aging', query: to.query }),
  },

  {
    path: '/cuentas-pagar/comprobantes/nuevo',
    redirect: (to) => ({ name: 'comprobante-new', query: to.query }),
  },

  {
    path: '/cuentas-pagar/pagos',
    redirect: (to) => ({ name: 'cuentas-pagar-pagos', query: to.query }),
  },

  {
    path: '/cuentas-pagar/configuracion',
    redirect: (to) => ({ name: 'cuentas-pagar-config', query: to.query }),
  },

  {
    path: '/cuentas-pagar/proveedores/:id/imputar',
    // The "Imputar pago" screen no longer exists; its old URL lands on the list of invoices to pay.
    redirect: () => ({ name: 'cuentas-pagar-aging' }),
  },

  {
    path: '/cuentas-pagar/proveedores/:id/pagos/:movimientoId/imprimir',
    redirect: (to) => ({ name: 'cuentas-pagar-pago-imprimir', params: to.params, query: to.query }),
  },

  // Vue Router's matcher scores more-specific (longer/more-static) paths
  // higher regardless of registration order, so '.../imputar' and
  // '.../imprimir' above still win over this shorter ':id' pattern.
  {
    path: '/cuentas-pagar/proveedores/:id',
    redirect: (to) => ({ name: 'cuentas-pagar-proveedor-detalle', params: to.params, query: to.query }),
  },
];
